import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { schoolPride } from "../lib/confetti";
import { PUZZLE, key, type Clue, type Dir } from "../lib/crossword";

const { solution, rows, cols, numberAt, whiteCells, across, down } = PUZZLE;
const isWhite = (r: number, c: number) => Boolean(solution[r]?.[c]);

type Cell = { r: number; c: number };
const hasAcross = (cell: Cell) => Boolean(findEntry(across, cell));
const hasDown = (cell: Cell) => Boolean(findEntry(down, cell));
// The direction actually usable at a cell: keep the requested one if that cell
// has a word there, otherwise fall back to the one it does have. Every white
// cell belongs to at least one entry, so this always returns a real direction.
// The immediately-adjacent cell in a direction, but only if it's white — used
// for typing/backspace so advancing stops at the end of a word instead of
// leaping over a black square into the next entry.
const adjacent = (r: number, c: number, dr: number, dc: number): Cell | null =>
  isWhite(r + dr, c + dc) ? { r: r + dr, c: c + dc } : null;
const effDir = (cell: Cell, d: Dir): Dir =>
  d === 'across'
    ? hasAcross(cell)
      ? 'across'
      : 'down'
    : hasDown(cell)
      ? 'down'
      : 'across';

// Auto-advance order when a word is completed: all acrosses, then all downs.
// Consecutive same-direction entries stay together (1-Down → 4-Down), and the
// last of a direction rolls into the other (last down → first across).
const ORDER: Clue[] = [...across, ...down];

// Lowest-numbered entry — where selection starts (and returns to on reset), so
// the clue bar and highlight are populated from the first render.
const FIRST = [...across, ...down].sort((a, b) => a.number - b.number)[0];
const START_CELL = FIRST?.cells[0] ?? { r: 0, c: 0 };
const START_DIR: Dir = FIRST?.dir ?? "across";

function fmt(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function Crossword({ guestId }: { guestId?: string }) {
  const [letters, setLetters] = useState<Record<string, string>>({});
  // Popup after the grid is first filled in: 'solved' or 'wrong'.
  const [result, setResult] = useState<"solved" | "wrong" | null>(null);
  // Whether we've already reacted to the current full grid (reset when a cell
  // is cleared), so the popup fires once per fill rather than every keystroke.
  const notified = useRef(false);
  const [active, setActive] = useState<{ r: number; c: number }>(START_CELL);
  const [dir, setDir] = useState<Dir>(START_DIR);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [endTime, setEndTime] = useState<number | null>(null);
  const [, tick] = useState(0);

  // First-entry correctness per cell, for the share card. Kept in a ref because
  // it never needs to trigger a re-render — only read on solve.
  const firstTry = useRef<Record<string, boolean>>({});
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});
  const activeRef = useRef(active);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const solved = endTime !== null;

  // Tick the timer once a second while solving.
  useEffect(() => {
    if (startTime === null || endTime !== null) return;
    const id = setInterval(() => tick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [startTime, endTime]);

  // Checked on every input: a correct grid always celebrates + logs (even if you
  // first filled it wrong and then fixed it in place). A full-but-wrong grid
  // nudges once — `notified` only suppresses repeat nudges, never the win.
  useEffect(() => {
    if (solved || startTime === null) return;

    const filled = whiteCells.every(
      (w) => (letters[key(w.r, w.c)] ?? "") !== "",
    );
    const correct =
      filled &&
      whiteCells.every((w) => (letters[key(w.r, w.c)] ?? "") === w.solution);

    if (correct) {
      const now = Date.now();
      setEndTime(now);
      setResult("solved");
      schoolPride();
      if (guestId) {
        void api
          .post("/games/crossword", {
            guestId,
            seconds: Math.round((now - startTime) / 1000),
          })
          .catch(() => {});
      }
      return;
    }

    if (!filled) {
      notified.current = false; // grid opened up again — allow a fresh nudge
      return;
    }
    if (notified.current) return; // already nudged for this full-but-wrong grid
    notified.current = true;
    setResult("wrong");
  }, [letters, solved, startTime, guestId]);

  const activeEntry = useMemo(() => {
    const d = effDir(active, dir);
    return findEntry(d === "across" ? across : down, active);
  }, [dir, active]);
  const activeKeys = useMemo(
    () => new Set((activeEntry?.cells ?? []).map((c) => key(c.r, c.c))),
    [activeEntry],
  );

  const focusCell = (r: number, c: number) => {
    const el = inputs.current[key(r, c)];
    if (el) {
      // preventScroll stops iOS from yanking the page to the input on every
      // auto-advance, which is what made the grid feel jittery/bouncy.
      el.focus({ preventScroll: true });
      el.select();
    }
  };

  // Focus the starting cell on mount so a physical keyboard can type right away
  // (the highlight alone wasn't focusing an input). Mobile still waits for a tap
  // — iOS won't open the soft keyboard without a user gesture.
  useEffect(() => {
    focusCell(START_CELL.r, START_CELL.c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const step = (r: number, c: number, dr: number, dc: number) => {
    let nr = r + dr;
    let nc = c + dc;
    while (nr >= 0 && nc >= 0 && nr < rows && nc < cols) {
      if (isWhite(nr, nc)) return { r: nr, c: nc };
      nr += dr;
      nc += dc;
    }
    return null;
  };

  // Core letter entry: set the cell, then move on. Shared by onBeforeInput (the
  // primary path — it fires even when the typed letter equals what's already in
  // a shared cell, which onChange skips) and onChange (a fallback).
  const typeLetter = (r: number, c: number, ch: string) => {
    const k = key(r, c);
    if (startTime === null) setStartTime(Date.now());
    setLetters((p) => ({ ...p, [k]: ch }));
    if (firstTry.current[k] === undefined) {
      firstTry.current[k] = ch === solution[r]?.[c];
    }
    // Advance along the direction the cell actually supports (a cell with no
    // down word must move across), so a letter always lands and steps forward.
    const d = effDir({ r, c }, dir);
    const next = d === "across" ? adjacent(r, c, 0, 1) : adjacent(r, c, 1, 0);
    if (next) {
      focusCell(next.r, next.c);
      return;
    }

    // At the physical end of the word. If the word still has gaps, jump to the
    // first empty one; if it's complete, move on to the next entry — same
    // direction while there is one (1-Down → 4-Down), then over to the other
    // (last down → first across), following ORDER.
    const entry = findEntry(d === "across" ? across : down, { r, c });
    if (!entry) return;
    const wordDone = entry.cells.every((cc) =>
      cc.r === r && cc.c === c ? true : Boolean(letters[key(cc.r, cc.c)]),
    );
    if (wordDone) {
      const i = ORDER.findIndex(
        (e) => e.number === entry.number && e.dir === entry.dir,
      );
      const nextEntry = ORDER[(i + 1) % ORDER.length];
      if (nextEntry) goToEntry(nextEntry);
    } else {
      const gap = entry.cells.find(
        (cc) => !(cc.r === r && cc.c === c) && !letters[key(cc.r, cc.c)],
      );
      if (gap) focusCell(gap.r, gap.c);
    }
  };

  // Primary letter path: fires on every keystroke even when the value is
  // unchanged (e.g. typing V onto a shared cell already holding V), which
  // onChange silently swallows. preventDefault keeps the field state-controlled.
  const handleBeforeInput = (
    r: number,
    c: number,
    e: React.FormEvent<HTMLInputElement>,
  ) => {
    const data = (e.nativeEvent as InputEvent).data ?? "";
    const ch = data.replace(/[^a-zA-Z]/g, "").slice(-1).toUpperCase();
    if (ch) {
      e.preventDefault();
      typeLetter(r, c, ch);
    }
  };

  // Fallback for anything not caught above (and deletions that empty the cell).
  const handleChange = (r: number, c: number, value: string) => {
    const ch = value.slice(-1).toUpperCase();
    if (!/[A-Z]/.test(ch)) {
      if (value === "") setLetters((p) => ({ ...p, [key(r, c)]: "" }));
      return;
    }
    typeLetter(r, c, ch);
  };

  const handleKeyDown = (r: number, c: number, e: React.KeyboardEvent) => {
    const k = key(r, c);
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowRight": {
        e.preventDefault();
        setDir("across");
        const n = step(r, c, 0, e.key === "ArrowLeft" ? -1 : 1);
        if (n) focusCell(n.r, n.c);
        break;
      }
      case "ArrowUp":
      case "ArrowDown": {
        e.preventDefault();
        setDir("down");
        const n = step(r, c, e.key === "ArrowUp" ? -1 : 1, 0);
        if (n) focusCell(n.r, n.c);
        break;
      }
      case "Backspace": {
        e.preventDefault();
        if (letters[k]) {
          setLetters((p) => ({ ...p, [k]: "" }));
        } else {
          const d = effDir({ r, c }, dir);
          const back =
            d === "across" ? adjacent(r, c, 0, -1) : adjacent(r, c, -1, 0);
          if (back) {
            setLetters((p) => ({ ...p, [key(back.r, back.c)]: "" }));
            focusCell(back.r, back.c);
          }
        }
        break;
      }
      case " ": {
        e.preventDefault();
        // Only flip direction where the cell is actually a crossing.
        if (hasAcross({ r, c }) && hasDown({ r, c })) {
          setDir((d) => (d === "across" ? "down" : "across"));
        }
        break;
      }
      default:
        break;
    }
  };

  const goToEntry = (entry: Clue) => {
    setDir(entry.dir);
    const target =
      entry.cells.find((c) => !letters[key(c.r, c.c)]) ?? entry.cells[0];
    if (target) {
      setActive(target);
      focusCell(target.r, target.c);
    }
  };

  const cycleClue = (delta: number) => {
    const list = dir === "across" ? across : down;
    const i = activeEntry
      ? list.findIndex((e) => e.number === activeEntry.number)
      : -1;
    const next = list[(i + delta + list.length) % list.length];
    if (next) goToEntry(next);
  };

  const reset = () => {
    setLetters({});
    firstTry.current = {};
    setStartTime(null);
    setEndTime(null);
    setActive(START_CELL);
    setDir(START_DIR);
  };

  const elapsed = startTime ? (endTime ?? Date.now()) - startTime : 0;

  return (
    <div className="mx-auto flex max-w-md flex-col items-center">
      {/* Timer */}
      <div className="mb-4 font-serif text-lg text-pine/60 lining-nums tabular-nums">
        {fmt(elapsed)}
      </div>

      {/* Clue bar */}
      <div className="mb-4 flex w-full items-stretch border border-pine/20">
        <ClueArrow
          label="Previous clue"
          onClick={() => cycleClue(-1)}
          dir="left"
        />
        <button
          type="button"
          onClick={() => {
            if (hasAcross(active) && hasDown(active)) {
              setDir((d) => (d === "across" ? "down" : "across"));
            }
          }}
          className="flex-1 px-4 py-3 text-center"
        >
          <span className="font-serif text-xs uppercase tracking-[0.18em] text-midnight-teal">
            {activeEntry ? `${activeEntry.number} ${activeEntry.dir}` : ""}
          </span>
          <span className="mt-1 block font-serif text-lg leading-snug text-pine">
            {activeEntry?.clue}
          </span>
        </button>
        <ClueArrow label="Next clue" onClick={() => cycleClue(1)} dir="right" />
      </div>

      {/* Grid — width-capped so 8 columns fit a phone; cells size off the 1fr
          columns and stay square. */}
      <div
        className="grid w-full max-w-sm select-none"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {solution.map((row, r) =>
          row.map((cell, c) => {
            const k = key(r, c);
            if (cell === null) {
              return <div key={k} className="aspect-square bg-cream" />;
            }
            const inEntry = activeKeys.has(k);
            const isActive = active.r === r && active.c === c;
            return (
              <div
                key={k}
                className={`relative aspect-square border border-pine/40 ${
                  isActive
                    ? "bg-blush/50"
                    : inEntry
                      ? "bg-dusty-teal/30"
                      : "bg-cream"
                }`}
              >
                {numberAt[k] && (
                  <span className="pointer-events-none absolute left-0.5 top-0 text-[0.6rem] leading-tight text-pine/60 lining-nums">
                    {numberAt[k]}
                  </span>
                )}
                <input
                  ref={(el) => {
                    inputs.current[k] = el;
                  }}
                  value={letters[k] ?? ""}
                  onBeforeInput={(e) => handleBeforeInput(r, c, e)}
                  onChange={(e) => handleChange(r, c, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(r, c, e)}
                  onFocus={(e) => {
                    setActive({ r, c });
                    // Snap direction to one this cell actually has, so an
                    // across-only cell never sits in a dead "down" state.
                    setDir((cur) => effDir({ r, c }, cur));
                    // Select the existing letter so the next keystroke overwrites
                    // it, no matter where a click drops the caret.
                    e.currentTarget.select();
                  }}
                  onPointerDown={() => {
                    // Re-tapping the active cell flips direction — but only at a
                    // real crossing.
                    if (
                      activeRef.current.r === r &&
                      activeRef.current.c === c &&
                      hasAcross({ r, c }) &&
                      hasDown({ r, c })
                    ) {
                      setDir((d) => (d === "across" ? "down" : "across"));
                    }
                  }}
                  disabled={solved}
                  aria-label={`Row ${r + 1}, column ${c + 1}`}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  className="h-full w-full bg-transparent text-center font-serif text-lg uppercase text-pine caret-transparent outline-none sm:text-2xl"
                />
              </div>
            );
          }),
        )}
      </div>

      {/* Clue lists */}
      <div className="mt-8 grid w-full grid-cols-2 gap-6 text-left">
        <ClueList
          title="Across"
          clues={across}
          active={activeEntry}
          onPick={goToEntry}
        />
        <ClueList
          title="Down"
          clues={down}
          active={activeEntry}
          onPick={goToEntry}
        />
      </div>

      <button
        type="button"
        onClick={reset}
        className="mt-8 font-serif text-sm uppercase tracking-[0.15em] text-pine/50 underline decoration-pine/20 underline-offset-4 transition-colors hover:text-pine/80"
      >
        Reset
      </button>

      {result && (
        <ResultModal
          kind={result}
          time={fmt(elapsed)}
          onClose={() => setResult(null)}
        />
      )}
    </div>
  );
}

function findEntry(list: Clue[], cell: { r: number; c: number }) {
  return list.find((e) =>
    e.cells.some((c) => c.r === cell.r && c.c === cell.c),
  );
}

function ClueArrow({
  label,
  onClick,
  dir,
}: {
  label: string;
  onClick: () => void;
  dir: "left" | "right";
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex w-11 items-center justify-center border-pine/20 text-pine/50 transition-colors hover:text-pine"
      style={
        dir === "left"
          ? { borderRight: "1px solid" }
          : { borderLeft: "1px solid" }
      }
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.25}
        className="h-4 w-4"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d={dir === "left" ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"}
        />
      </svg>
    </button>
  );
}

function ClueList({
  title,
  clues,
  active,
  onPick,
}: {
  title: string;
  clues: Clue[];
  active: Clue | undefined;
  onPick: (c: Clue) => void;
}) {
  return (
    <div>
      <p className="mb-2 font-serif text-sm font-bold uppercase tracking-[0.18em] text-midnight-teal">
        {title}
      </p>
      <ul className="space-y-1.5">
        {clues.map((clue) => {
          const on = active?.dir === clue.dir && active?.number === clue.number;
          return (
            <li key={`${clue.dir}-${clue.number}`}>
              <button
                type="button"
                onClick={() => onPick(clue)}
                className={`flex gap-2 text-left text-sm leading-snug transition-colors ${
                  on ? "text-pine" : "text-pine/60 hover:text-pine"
                }`}
              >
                <span className="font-bold lining-nums">{clue.number}</span>
                <span>{clue.clue}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** NYT-style popup shown the first time the grid is filled: a win with the
 *  time, or a "not quite" nudge that dismisses so you can keep going. */
function ResultModal({
  kind,
  time,
  onClose,
}: {
  kind: "solved" | "wrong";
  time: string;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-pine/40 px-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm border border-pine/15 bg-cream px-8 py-10 text-center shadow-invite"
        onClick={(e) => e.stopPropagation()}
      >
        <span
          aria-hidden="true"
          className="font-serif text-3xl text-dusty-rose"
        >
          ❃
        </span>
        {kind === "solved" ? (
          <>
            <h3 className="mt-3 font-serif text-4xl text-pine">
              Solved in {time}!
            </h3>
            <p className="mt-3 font-sans text-base text-pine/70">
              Nicely done.
            </p>
          </>
        ) : (
          <>
            <h3 className="mt-3 font-serif text-4xl text-pine">Not quite…</h3>
            <p className="mt-3 font-sans text-base text-pine/70">
              At least one square is wrong. Try again!
            </p>
          </>
        )}
        <button
          type="button"
          onClick={onClose}
          className="mt-6 bg-midnight-teal px-8 py-3 font-sans text-sm font-medium uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pine"
        >
          {kind === "solved" ? "Nice" : "Keep trying"}
        </button>
      </div>
    </div>
  );
}
